// MARK://dhk8/pos/features/PaymentDialog
// Cobro de orden — propina (persistida en el backend, el total la incluye) + tenders
// mixtos + cierre. El backend (dhk8-pos-orders) cierra la orden cuando paidCents ≥
// totalCents; aquí solo registramos los pagos. Reescrito sobre @dockethub/ui-restaurant
// (<TipSelector>, <ChangeDueDisplay>) — Sprint E.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Printer, ExternalLink } from 'lucide-react'
import { Button, Dialog } from '@dockethub/ui'
import { TipSelector, ChangeDueDisplay } from '@dockethub/ui-restaurant'
import { useOrderStore, type PaymentMethod, type RegisteredPayment } from '../stores/order-store.js'
import { useAuthStore } from '../stores/auth-store.js'
import { pesosStringToCents, formatCents } from '../lib/money.js'
import { useEnqueueReceipt } from '../hooks/usePrinting.js'
import { renderReceipt } from '../lib/print/templates.js'
import { getReceiptOpts } from '../lib/print/receipt-opts.js'
import { getPrintingConfig, pushPrintHistory } from '../lib/bridge/printing-config-store.js'
import { kickDrawer, printReceipt } from '../lib/bridge/bridge-client.js'
import { trackRelayJob } from '../lib/bridge/relay-job-tracker.js'
import { toReceipt } from '../lib/bridge/canonical-receipt.js'

const METHOD_TABS: readonly { id: PaymentMethod; label: string }[] = [
  { id: 'CASH', label: 'Efectivo' },
  { id: 'CARD_DEBIT', label: 'Tarjeta' },
  { id: 'TRANSFER', label: 'Transferencia' },
  { id: 'MEAL_VOUCHER', label: 'Vales' },
]
const METHOD_LABEL: Record<string, string> = { CASH: 'Efectivo', CARD_DEBIT: 'Tarjeta débito', CARD_CREDIT: 'Tarjeta crédito', TRANSFER: 'Transferencia', MEAL_VOUCHER: 'Vale', OTHER: 'Otro' }

export interface PaymentDialogProps {
  open: boolean
  onClose: () => void
  onClosed?: () => void
  /** When set, the persistent payment is tagged to this sub-account (the order total
   *  is still what's covered — per-sub-account split settlement is a future iteration). */
  subAccountId?: string | null
  /** A dónde navegar al cerrar el ticket de "Venta cerrada". Default '/floor'
   *  (Comandas vuelve al plano). Barra pasa `null` para QUEDARSE en /pos/barra
   *  (venta rápida: listo para el siguiente cliente sin salir de la pantalla). */
  redirectAfterTicket?: string | null
  /** Si el diálogo auto-imprime el recibo al cerrar la orden (config `autoPrint.comanda`).
   *  Default true (Comandas). Barra pasa `false`: su cierre imprime aparte con la
   *  config `autoPrint.barra` en su propio onClosed — sin este flag saldría DOBLE
   *  ticket (comanda + barra). El drawer per-tender NO se toca (sigue en ambos). */
  autoPrintReceipt?: boolean
}

export default function PaymentDialog({ open, onClose, onClosed, subAccountId, redirectAfterTicket = '/floor', autoPrintReceipt = true }: PaymentDialogProps) {
  const navigate = useNavigate()
  const order = useOrderStore((s) => s.order)
  const payments = useOrderStore((s) => s.payments)
  const registerPayment = useOrderStore((s) => s.registerPayment)
  const setTip = useOrderStore((s) => s.setTip)

  const [method, setMethod] = useState<PaymentMethod>('CASH')
  const [amountInput, setAmountInput] = useState('')
  const [cashGivenInput, setCashGivenInput] = useState('')
  const [cardLastFour, setCardLastFour] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showTicket, setShowTicket] = useState(false)

  // Tip is persisted on the order (the total includes it) — debounce the API call.
  const tipTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  function handleTipChange(tipCents: number) {
    if (tipTimer.current) clearTimeout(tipTimer.current)
    tipTimer.current = setTimeout(() => { void setTip(tipCents).catch(() => undefined) }, 500)
  }
  useEffect(() => () => { if (tipTimer.current) clearTimeout(tipTimer.current) }, [])

  // Preseleccionar el método de pago que el comensal eligió al ordenar (flujo
  // "aceptar sin pago" → "cobrar al recoger"): CASH→Efectivo, TERMINAL→Tarjeta.
  // ONLINE no mapea a un tender de mostrador → se deja el default. Sólo al ABRIR
  // (no en cada render) para no pisar la selección manual del cajero.
  useEffect(() => {
    if (!open) return
    const chosen = order?.chosenPaymentMethod
    const mapped: PaymentMethod | null =
      chosen === 'CASH' ? 'CASH' : chosen === 'TERMINAL' ? 'CARD_DEBIT' : null
    if (mapped) setMethod(mapped)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order?.chosenPaymentMethod])

  // 2026-05-25: TODOS los hooks deben declararse ANTES de cualquier early
  // return o React tira #310 ("Rendered fewer hooks than expected") cuando
  // order pasa de null → object entre renders. Antes el useMemo/useEnqueueReceipt
  // estaban DESPUÉS del `if (!open || !order) return null` → en primer render
  // se devolvía null (0 hooks útiles) y al siguiente con order activo se
  // llamaban 3 hooks → crash. Reordenamos: hooks primero, early return después.
  const amountCents = useMemo(() => pesosStringToCents(amountInput), [amountInput])
  const cashGivenCents = useMemo(() => pesosStringToCents(cashGivenInput), [cashGivenInput])
  const enqueueReceipt = useEnqueueReceipt()
  const tenantIdActive = useAuthStore((s) => s.activeTenantId())

  // 2026-05-25: Modal post-cobro branded (mismo HTML que FastBarPage muestra
  // en /pos/barra "Venta cerrada"). Hidrata cuando showTicket=true con el
  // ticket completo: logo SAVIs + dirección + RFC + régimen + items con
  // modifiers + IVA desglosado + QR a tickets.nintaigo.com.
  const [receiptHtml, setReceiptHtml] = useState<string>('')
  useEffect(() => {
    if (!showTicket || !order) { setReceiptHtml(''); return }
    let alive = true
    const opts = getReceiptOpts(tenantIdActive)
    void renderReceipt(order, opts).then((html) => { if (alive) setReceiptHtml(html) })
    return () => { alive = false }
  }, [showTicket, order, tenantIdActive])

  if (!open || !order) return null

  const preTipCents = order.subtotalCents + order.taxCents - order.discountCents
  const totalCents = order.totalCents // includes tip
  const paidCents = order.paidCents ?? 0
  const remaining = Math.max(0, totalCents - paidCents)
  const isCovered = order.status === 'CLOSED' || (paidCents >= totalCents && totalCents > 0)

  const effectiveAmountCents = method === 'CASH'
    ? (cashGivenCents > 0 ? Math.min(cashGivenCents, remaining || cashGivenCents) : 0)
    : amountCents
  const changeCents = method === 'CASH' && cashGivenCents > 0 ? Math.max(0, cashGivenCents - (remaining || 0)) : 0

  function resetInputs() { setAmountInput(''); setCashGivenInput(''); setCardLastFour('') }

  async function handleAddPayment() {
    setError(null)
    if (effectiveAmountCents <= 0) { setError('Ingresa un monto válido'); return }
    setBusy(true)
    try {
      await registerPayment({
        method,
        amountCents: effectiveAmountCents,
        ...(method === 'CASH' && cashGivenCents > 0 ? { cashGivenCents, changeCents } : {}),
        ...(cardLastFour ? { cardLastFour } : {}),
        ...(subAccountId ? { subAccountId } : {}),
      })
      resetInputs()
      // Bridge side-effects (best-effort, NUNCA bloqueamos el flujo de cobro):
      //   1. Apertura de cajón si el método tiene drawerOnPayment[method] = true
      //   2. Auto-print "comanda" si autoPrint.comanda.enabled (la mesa cerró)
      const closedNow = useOrderStore.getState().order?.status === 'CLOSED'
      void runBridgeAfterPayment(method, useOrderStore.getState().order, tenantIdActive, closedNow, autoPrintReceipt)
      // if that closed the order, jump straight to the ticket
      if (closedNow) { setShowTicket(true); onClosed?.() }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo registrar el pago')
    } finally {
      setBusy(false)
    }
  }

  async function handlePrint() {
    if (!order) return
    try { await enqueueReceipt.mutateAsync(order.id) } catch { /* best-effort */ }
  }

  function handleCloseDialog() {
    if (showTicket) {
      setShowTicket(false)
      // Comandas navega al plano; Barra pasa null para quedarse en /pos/barra.
      if (redirectAfterTicket !== null) navigate(redirectAfterTicket)
    }
    onClose()
  }

  if (showTicket) {
    // Modal post-cobro: mismo HTML branded que FastBarPage (renderReceipt
    // → logo + RFC + régimen + items con modifiers + IVA desglosado + QR).
    // Reemplaza el TicketPreview viejo (sin branding, IVA $0, botones
    // overlapped). 3 botones consistentes: Imprimir / Ver en línea / Cerrar.
    const ticketPublicUrl = order.publicTicketUuid
      ? `https://tickets.nintaigo.com/${order.publicTicketUuid}`
      : null
    // 2026-05-25: usar <Dialog> de @dockethub/ui (mismo componente que
    // FastBarPage Lane Tickets v2 monta en /pos/barra) en vez del HTML
    // manual con divs custom. Esto garantiza idéntica apariencia entre
    // ambos puntos de cobro: card blanca, header con X cierre, footer
    // estilizado con Button del design system.
    return (
      <Dialog
        open
        onClose={handleCloseDialog}
        title={`Venta cerrada · ${order.folio}`}
        footer={
          <>
            <Button variant="primary" size="md" leading={<Printer size={14} />}
              onClick={handlePrint} disabled={enqueueReceipt.isPending}>
              {enqueueReceipt.isPending ? 'Imprimiendo…' : 'Imprimir ticket'}
            </Button>
            {ticketPublicUrl && (
              <Button variant="secondary" size="md" leading={<ExternalLink size={14} />}
                onClick={() => window.open(ticketPublicUrl, '_blank', 'noopener')}>
                Ver en línea
              </Button>
            )}
            <Button variant="secondary" size="md" onClick={handleCloseDialog}>
              Cerrar
            </Button>
          </>
        }
      >
        <div className="dhk8-ticket-wrap dhk8-ticket-print-root"
             dangerouslySetInnerHTML={{ __html: receiptHtml }} />
      </Dialog>
    )
  }

  return (
    <div className="dhk8-dialog-overlay" onClick={handleCloseDialog}>
      <div className="dhk8-payment-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dhk8-dialog__title">Cobrar orden · {order.folio}</div>

        {/* Totales */}
        <section className="dhk8-payment-totals">
          <div className="dhk8-payment-totals__row"><span>Subtotal</span><span>{formatCents(order.subtotalCents)}</span></div>
          <div className="dhk8-payment-totals__row"><span>IVA</span><span>{formatCents(order.taxCents)}</span></div>
          {order.discountCents > 0 && <div className="dhk8-payment-totals__row"><span>Descuento</span><span>−{formatCents(order.discountCents)}</span></div>}
          {order.tipCents > 0 && <div className="dhk8-payment-totals__row"><span>Propina</span><span>{formatCents(order.tipCents)}</span></div>}
          <div className="dhk8-payment-totals__row dhk8-payment-totals__row--total"><span>Total a cobrar</span><span>{formatCents(totalCents)}</span></div>
          <div className="dhk8-payment-totals__row"><span>Pagado</span><span>{formatCents(paidCents)}</span></div>
          <div className="dhk8-payment-totals__row"><span>Restante</span><span className={remaining > 0 ? 'dhk8-payment-totals__remaining' : ''}>{formatCents(remaining)}</span></div>
        </section>

        {/* Propina */}
        <section className="dhk8-payment-section">
          <div className="dhk8-payment-section__title">Propina</div>
          <TipSelector baseCents={preTipCents} valueCents={order.tipCents} onChange={handleTipChange} disabled={busy} />
        </section>

        {/* Pagos registrados */}
        <section className="dhk8-payment-section">
          <div className="dhk8-payment-section__title">
            Pagos registrados
            <span className={`dhk8-payment-badge${isCovered ? ' dhk8-payment-badge--ok' : ''}`}>{payments.length} {payments.length === 1 ? 'pago' : 'pagos'}</span>
          </div>
          {payments.length === 0 ? (
            <div className="dhk8-payment-empty">Sin pagos aún.</div>
          ) : (
            <ul className="dhk8-payment-list">{payments.map((p) => <PaymentRow key={p.id} payment={p} />)}</ul>
          )}
        </section>

        {/* Agregar pago */}
        {!isCovered && (
          <section className="dhk8-payment-add">
            <div className="dhk8-payment-section__title">Agregar pago</div>
            <div className="dhk8-payment-tabs">
              {METHOD_TABS.map((t) => (
                <button key={t.id} type="button" className={`dhk8-method-tab${method === t.id ? ' dhk8-method-tab--on' : ''}`}
                  onClick={() => { setMethod(t.id); resetInputs() }}>{t.label}</button>
              ))}
            </div>
            <div className="dhk8-payment-form">
              {method === 'CASH' ? (
                <>
                  <div className="dhk8-payment-field">
                    <label>Efectivo recibido</label>
                    <input type="text" inputMode="decimal" value={cashGivenInput} onChange={(e) => setCashGivenInput(e.target.value)} placeholder="0.00" autoFocus className="dhk8-payment-input" />
                  </div>
                  <div className="dhk8-payment-presets">
                    <button type="button" className="dhk8-preset-btn" onClick={() => setCashGivenInput((remaining / 100).toFixed(2))}>Exacto ({formatCents(remaining)})</button>
                    {[50000, 20000, 10000].map((v) => (
                      <button key={v} type="button" className="dhk8-preset-btn" onClick={() => setCashGivenInput((v / 100).toFixed(2))}>{formatCents(v)}</button>
                    ))}
                  </div>
                  {cashGivenCents > 0 && <ChangeDueDisplay cashGivenCents={cashGivenCents} amountDueCents={remaining || effectiveAmountCents} />}
                </>
              ) : (
                <>
                  <div className="dhk8-payment-field">
                    <label>Monto</label>
                    <input type="text" inputMode="decimal" value={amountInput} onChange={(e) => setAmountInput(e.target.value)} placeholder="0.00" autoFocus className="dhk8-payment-input" />
                  </div>
                  <div className="dhk8-payment-presets">
                    <button type="button" className="dhk8-preset-btn" onClick={() => setAmountInput((remaining / 100).toFixed(2))}>Restante ({formatCents(remaining)})</button>
                  </div>
                  {method === 'CARD_DEBIT' && (
                    <div className="dhk8-payment-field">
                      <label>Últimos 4 dígitos (opcional)</label>
                      <input type="text" inputMode="numeric" maxLength={4} value={cardLastFour} onChange={(e) => setCardLastFour(e.target.value.replace(/\D/g, ''))} placeholder="1234" className="dhk8-payment-input" />
                    </div>
                  )}
                </>
              )}
              <button type="button" className="dhk8-payment-add-btn" disabled={busy || effectiveAmountCents <= 0} onClick={() => void handleAddPayment()}>
                {busy ? 'Registrando…' : `Agregar ${formatCents(effectiveAmountCents)}`}
              </button>
            </div>
          </section>
        )}

        {error && <div className="dhk8-payment-error">{error}</div>}

        <div className="dhk8-dialog__actions">
          <button type="button" onClick={handleCloseDialog} disabled={busy}>Cancelar</button>
          <button type="button" className="dhk8-dialog__btn--primary" onClick={() => { setShowTicket(true); onClosed?.() }} disabled={!isCovered || busy}>
            {isCovered ? 'Ver ticket' : `Faltan ${formatCents(remaining)}`}
          </button>
        </div>
      </div>
      <style>{styles}</style>
    </div>
  )
}

/** Side-effects opcionales contra el bridge tras un cobro exitoso:
 *   - Si drawerOnPayment[method] = true → POST /v1/drawer/kick
 *   - Si la orden cerró y autoPrint.comanda.enabled → POST /v1/print/receipt
 *
 * NUNCA throw: el cobro ya pasó al backend, esto es enriquecimiento. Cualquier
 * fallo se registra en el history del store (sección 5 de PrintingSettings)
 * para que el cajero pueda diagnosticar sin romper la línea de caja. */
async function runBridgeAfterPayment(
  method: PaymentMethod,
  order: import('../stores/order-store.js').OrderDto | null,
  tenantId: string | null | undefined,
  closedNow: boolean,
  autoPrintReceipt: boolean,
): Promise<void> {
  try {
    const cfg = getPrintingConfig()
    if (cfg.drawerOnPayment[method as keyof typeof cfg.drawerOnPayment]) {
      kickDrawer({ printer: cfg.defaultPrinter }).then(
        // relay: jobId presente → sigue el progreso (el tracker escribe el history
        // real). host: sin jobId → resolvió al disparar el cajón, history 'ok' aquí.
        (r) => { if (r.jobId) trackRelayJob(r.jobId, 'Cajón', 'drawer'); else pushPrintHistory('drawer', 'ok') },
        (e: unknown) => pushPrintHistory('drawer', 'error', e instanceof Error ? e.message : String(e)),
      )
    }
    if (closedNow && order && autoPrintReceipt && cfg.autoPrint.comanda.enabled) {
      const header = getReceiptOpts(tenantId)
      const payload = toReceipt(order, { header, copies: cfg.autoPrint.comanda.copies })
      printReceipt(payload, { printer: cfg.defaultPrinter }).then(
        (r) => { if (r.jobId) trackRelayJob(r.jobId, 'Ticket', 'receipt'); else pushPrintHistory('receipt:comanda', 'ok') },
        (e: unknown) => pushPrintHistory('receipt:comanda', 'error', e instanceof Error ? e.message : String(e)),
      )
    }
  } catch (e) {
    // safety net — cualquier error sintáctico/lookup no debe propagar.
    pushPrintHistory('post-payment', 'error', e instanceof Error ? e.message : String(e))
  }
}

function PaymentRow({ payment }: { payment: RegisteredPayment }) {
  return (
    <li className="dhk8-payment-list__row">
      <span>{METHOD_LABEL[payment.method] ?? payment.method}{payment.cardLastFour ? ` ****${payment.cardLastFour}` : ''}</span>
      <span>{formatCents(payment.amountCents)}</span>
    </li>
  )
}

const styles = `
  .dhk8-payment-dialog { background:var(--pos-surface); border:1px solid var(--pos-separator); padding:var(--pos-space-5); display:flex; flex-direction:column; gap:var(--pos-space-4); min-width:420px; max-width:560px; max-height:90vh; overflow-y:auto; border-radius:var(--pos-radius-xl); box-shadow:var(--pos-shadow-4); }
  .dhk8-payment-totals { display:flex; flex-direction:column; gap:var(--pos-space-1); padding:var(--pos-space-4); background:var(--pos-elevated); border:1px solid var(--pos-separator); border-radius:var(--pos-radius-lg); }
  .dhk8-payment-totals__row { display:flex; justify-content:space-between; font-size:var(--pos-text-sm); font-variant-numeric:tabular-nums; color:var(--pos-fg-secondary); }
  .dhk8-payment-totals__row--total { font-size:var(--pos-text-xl); font-weight:var(--pos-weight-semibold); color:var(--pos-fg); padding-top:var(--pos-space-3); border-top:1px solid var(--pos-separator); margin-top:var(--pos-space-2); }
  .dhk8-payment-totals__remaining { color:var(--pos-warning); }
  .dhk8-payment-section { display:flex; flex-direction:column; gap:var(--pos-space-2); }
  .dhk8-payment-section__title { display:flex; justify-content:space-between; align-items:center; font-size:var(--pos-text-xs); letter-spacing:var(--pos-tracking-widest); text-transform:uppercase; color:var(--pos-fg-tertiary); }
  .dhk8-payment-tabs { display:flex; flex-wrap:wrap; gap:var(--pos-space-2); }
  .dhk8-method-tab { padding:var(--pos-space-2) var(--pos-space-3); min-height:var(--pos-touch-target); border:1px solid var(--pos-separator-strong); background:transparent; color:var(--pos-fg-secondary); font-size:var(--pos-text-sm); font-weight:var(--pos-weight-medium); cursor:pointer; border-radius:var(--pos-radius-pill); transition:all var(--pos-duration-fast) ease; }
  .dhk8-method-tab:hover { background:var(--pos-elevated); }
  .dhk8-method-tab--on { background:var(--pos-tint); color:#fff; border-color:var(--pos-tint); box-shadow:var(--pos-shadow-1); }
  .dhk8-payment-add { border:1px solid var(--pos-separator); padding:var(--pos-space-4); background:var(--pos-elevated); display:flex; flex-direction:column; gap:var(--pos-space-3); border-radius:var(--pos-radius-lg); }
  .dhk8-payment-form { display:flex; flex-direction:column; gap:var(--pos-space-3); margin-top:var(--pos-space-2); }
  .dhk8-payment-field { display:flex; flex-direction:column; gap:var(--pos-space-2); }
  .dhk8-payment-field label { font-size:var(--pos-text-xs); letter-spacing:.02em; color:var(--pos-fg-secondary); }
  .dhk8-payment-input { border:1px solid var(--pos-separator-strong); padding:var(--pos-space-3); min-height:var(--pos-touch-target); background:var(--pos-canvas); color:var(--pos-fg); font-size:var(--pos-text-md); font-variant-numeric:tabular-nums; outline:none; width:100%; border-radius:var(--pos-radius-md); transition:border-color var(--pos-duration-fast) ease, box-shadow var(--pos-duration-fast) ease; }
  .dhk8-payment-input:focus { border-color:var(--pos-tint); box-shadow:0 0 0 3px var(--pos-tint-soft); }
  .dhk8-payment-presets { display:flex; flex-wrap:wrap; gap:var(--pos-space-2); }
  .dhk8-preset-btn { padding:var(--pos-space-2) var(--pos-space-3); min-height:var(--pos-touch-target); border:1px solid var(--pos-separator-strong); background:transparent; color:var(--pos-fg-secondary); font-size:var(--pos-text-sm); font-variant-numeric:tabular-nums; cursor:pointer; border-radius:var(--pos-radius-pill); transition:all var(--pos-duration-fast) ease; }
  .dhk8-preset-btn:hover { background:var(--pos-elevated); color:var(--pos-fg); }
  .dhk8-payment-add-btn { padding:var(--pos-space-3); min-height:var(--pos-touch-target-lg); background:var(--pos-tint); color:#fff; border:none; font-size:var(--pos-text-md); font-weight:var(--pos-weight-semibold); letter-spacing:.01em; cursor:pointer; border-radius:var(--pos-radius-lg); box-shadow:var(--pos-shadow-2); transition:filter var(--pos-duration-fast) ease, transform var(--pos-duration-fast) ease; }
  .dhk8-payment-add-btn:hover:not(:disabled) { filter:brightness(1.06); }
  .dhk8-payment-add-btn:active:not(:disabled) { transform:scale(.98); }
  .dhk8-payment-add-btn:disabled { opacity:.4; cursor:not-allowed; }
  .dhk8-payment-list { list-style:none; margin:0; padding:0; }
  .dhk8-payment-list__row { display:flex; justify-content:space-between; padding:var(--pos-space-3); border-bottom:1px solid var(--pos-separator); font-size:var(--pos-text-sm); font-variant-numeric:tabular-nums; }
  .dhk8-payment-list__row:last-child { border-bottom:none; }
  .dhk8-payment-empty { padding:var(--pos-space-4); color:var(--pos-fg-tertiary); font-size:var(--pos-text-sm); text-align:center; border:1px dashed var(--pos-separator-strong); border-radius:var(--pos-radius-lg); }
  .dhk8-payment-badge { font-size:var(--pos-text-xxs); padding:2px var(--pos-space-2); border:1px solid var(--pos-separator); background:var(--pos-elevated); border-radius:var(--pos-radius-pill); }
  .dhk8-payment-badge--ok { border-color:color-mix(in oklch, var(--pos-tint) 50%, transparent); color:var(--pos-success); background:var(--pos-tint-soft); }
  .dhk8-payment-error { color:var(--pos-danger); background:color-mix(in oklch, var(--pos-danger) 14%, transparent); border:1px solid color-mix(in oklch, var(--pos-danger) 40%, transparent); padding:var(--pos-space-3); font-size:var(--pos-text-sm); border-radius:var(--pos-radius-md); }
  .dhk8-ticket-wrap { display:flex; justify-content:center; padding:var(--pos-space-4); background:var(--pos-elevated); border:1px solid var(--pos-separator); max-height:50vh; overflow-y:auto; border-radius:var(--pos-radius-lg); }
  .dhk8-dialog-overlay { position:fixed; inset:0; background:var(--pos-overlay); display:flex; align-items:center; justify-content:center; z-index:var(--pos-z-modal, 100); padding:var(--pos-space-4); }
  .dhk8-dialog__title { font-family:var(--font-display, system-ui); font-size:var(--pos-text-xl); font-weight:var(--pos-weight-semibold); color:var(--pos-fg); }
  .dhk8-dialog__actions { display:flex; justify-content:flex-end; gap:var(--pos-space-2); }
  .dhk8-dialog__actions button { padding:var(--pos-space-3) var(--pos-space-4); min-height:var(--pos-touch-target); border:1px solid var(--pos-separator-strong); background:transparent; color:var(--pos-fg); font-size:var(--pos-text-md); font-weight:var(--pos-weight-medium); cursor:pointer; border-radius:var(--pos-radius-md); transition:background var(--pos-duration-fast) ease; }
  .dhk8-dialog__actions button:hover:not(:disabled) { background:var(--pos-elevated); }
  .dhk8-dialog__btn--primary { background:var(--pos-tint); color:#fff; border-color:var(--pos-tint); box-shadow:var(--pos-shadow-2); }
  .dhk8-dialog__btn--primary:hover:not(:disabled) { filter:brightness(1.06); }
  .dhk8-dialog__btn--primary:disabled { opacity:.4; cursor:not-allowed; }
`
